import { SlotEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { getBoundingClientRect } from '@mbrtargeting/metatag-utils';
import { Dimensions, DimensionsSource } from '../../interfaces/interfaces.js';
import { Registry } from '../../loader/di/registry.js';
import { selectLayoutSettings, selectPositionSettings } from '../../loader/essentials/config-selector.js';
import { triggerEvent, triggerHTMLElementEvent } from '../../loader/essentials/events.js';
import { logger } from '../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../loader/token.js';
import { isSingleSizeArray } from '../../utils/adsize-helper.js';
import { EffectFunction } from './index.js';

const log = logger.logGroup({ prefix: 'CLS Effect' });

export const CLS: EffectFunction = (adslot) => {
    const { slotName, containerNode, slotElementPromise, slotHull, adserverAnchor, options, dimensions } = adslot;
    const configResolver = Registry.lookup(CONFIG_RESOLVER);

    const { useStrictMinHeight = false } = configResolver.get(selectLayoutSettings());
    const { keepWidthFluid = true } = configResolver.get(selectPositionSettings(slotName)) || {};

    /** Gets all the available sizes of the enabled formats and find the largest one as dimension for the slot stabilization. */
    const getFormatMaxDimensions = (): Dimensions => {
        // use only [width,height]-sizes and get the largest one by sorting the list descending by height, secondary descending by width
        const [width = 0, height = 0] = adslot.getSizes().filter(isSingleSizeArray).sort(([w1, h1], [w2, h2]) => h2 === h1 ? w2 - w1 : h2 - h1).shift() || [];
        // use the largest one as dimension
        return { width, height };
    };

    /** Evaluate the css rules on the slotElement to calculate a possible dimension for the slot stabilization. */
    const getCssDimensions = (slotElement: HTMLElement): Dimensions => {
        const { width, height } = getBoundingClientRect(slotElement);
        return { width: Math.round(width), height: Math.round(height) };
    };

    /** Get the dimension set by publisher via adslot configure */
    const getPublisherDimensions = (): Dimensions => {
        return { width: options.fixedWidth, height: options.fixedHeight };
    };

    slotElementPromise.then((slotElement) => {

        /** stabilize the adslot to given dimensions */
        const stabilize = ({ width, height }: Dimensions) => window.requestAnimationFrame(() => {
            const style = adserverAnchor.style;
            triggerEvent(SystemSlotEvent.SDG_SLOT_SIZE_AVAILABLE, {
                passedObject: adslot,
                placement: adslot,
                slot: adslot.slotName,
                position: adslot.slotName,
                slotName: adslot.slotName,
            });
            triggerHTMLElementEvent(adslot.containerNode.parentNode, SlotEvent.SLOT_SIZE_AVAILABLE, {
                passedObject: adslot,
                placement: adslot,
                slot: adslot.slotName,
                position: adslot.slotName,
                slotName: adslot.slotName,
            });

            if (!keepWidthFluid) style.minWidth = `${width}px`;
            if (useStrictMinHeight) style.minHeight = `${height}px`; else style.height = `${height}px`;
            dimensions[DimensionsSource.CURRENT] = { width, height };
            log.info(`stabilize ${slotName} to dimension ${width}x${height} (%o)`, [dimensions]);
        });

        /** did we already evaluate the initial dimensions and setup the stabilization before?  */
        let firstTimeSetupDone: boolean = false;

        /** handler triggered by intersection observer or creative delivery to check if we need to stabilize the adslot */
        const check = (): void => {

            const currentDimensions = dimensions[DimensionsSource.CURRENT];
            if (!firstTimeSetupDone) {
                firstTimeSetupDone = true;

                slotHull.classList.add('sdgStrictSlot');

                dimensions[DimensionsSource.FORMAT] = getFormatMaxDimensions();
                dimensions[DimensionsSource.CSS] = getCssDimensions(slotElement);
                dimensions[DimensionsSource.PUBLISHER] = getPublisherDimensions();

                const initialDimensions: Dimensions = evaluateInitialDimensionsInPage(dimensions);
                const isTooSmall: boolean = currentDimensions.height < initialDimensions.height;
                if (isTooSmall) {
                    stabilize(initialDimensions);
                }
            } else {
                const creativeDimensions = dimensions[DimensionsSource.CREATIVE];
                //keep width always on default values, to not re-position the adslot (since we use "flex", which reacts to width)
                const newDimension: Dimensions = {
                    width: dimensions[DimensionsSource.DEFAULT].width,
                    height: dimensions[DimensionsSource.CREATIVE].height
                };
                const isTooSmall: boolean = currentDimensions.height < creativeDimensions.height;
                const slotAlreadyInRequestedSize: boolean = currentDimensions.height === newDimension.height && currentDimensions.width === newDimension.width;
                if (isTooSmall || (!slotAlreadyInRequestedSize && creativeDimensions.priorityRequest)) {
                    log.info(`re-size ${slotName} (too small / priority request) to  %o`, [creativeDimensions]);
                    stabilize(newDimension);
                }
            }
        };

        /** triggered when adslot is above or in viewport */
        const intersectionObserverCallback = (entries: IntersectionObserverEntry[]): void => {
            const isIntersecting: boolean = entries.some(entry => entry.isIntersecting);
            if (isIntersecting) {
                check();
            }
        };

        const observer = new IntersectionObserver(intersectionObserverCallback, {
            root: containerNode.ownerDocument,
            rootMargin: '100% 0% 0% 0%',
            threshold: 0,
        });
        observer.observe(containerNode);

        window.addEventListener(SystemSlotEvent.SDG_SLOT_CREATIVE_AVAILABLE, ({ detail }) => {
            if (slotName === detail.slotName) {
                // when the slot was above or in view before (firstTimeSetupDone is true), we check if we need to update the stabilized dimension
                if (firstTimeSetupDone) {
                    check();
                }
            }
        });
    });

    return () => {
        // TODO can not disconnect from observer or remove sdgStrictSlot as it is added delayed (should not remove before adding)
    };
};

/**
 * Will calculate the (theoretical) dimensions of the adSlot inside the web page.
 * Since we don't know how large the slot container will be at the end, we will have to work with some derived information.
 *
 * This calculation is only done the first time and slot is stabilized to the wining height.
 * Further changes to the slot height have to be requested and checked against some rules, see requestHeightChange()
 *
 * If a creative is delivered, only consider its dimensions if they make sense (height has to be higher than 5)
 *
 * @param dimensions dimensions per source
 * @returns a dimesion to stabilize the adslot
 */
export const evaluateInitialDimensionsInPage = (dimensions: Record<DimensionsSource, Dimensions>): Dimensions => {
    const { DEFAULT, CREATIVE, CSS, FORMAT, PUBLISHER } = DimensionsSource;
    // css size is base, format overwrites if bigger
    let leadingSize: DimensionsSource = (dimensions[CSS].height < dimensions[FORMAT].height) ? FORMAT : CSS;
    // valid creative overwrites, dont consider heights like 1x1
    leadingSize = (dimensions[CREATIVE].height > 5) ? CREATIVE : leadingSize;
    // valid creative still overwrites, even if publisher has other plans by setting a height. Otherwise, publisher leads.
    leadingSize = (leadingSize !== CREATIVE && dimensions[PUBLISHER].height > 0) ? PUBLISHER : leadingSize;
    // at last, check wining height against slot default. Higher number wins.
    leadingSize = (dimensions[leadingSize].height > dimensions[DEFAULT].height) ? leadingSize : DEFAULT;
    return {
        width: dimensions[DEFAULT].width, //use smallest, possible width, since we are using min-width and width does not contribute to cls
        height: dimensions[leadingSize].height,
    };
};
